/**
 * CheckoutOwnershipHold.tsx — the screen a signed-in patient sees when orders
 * S0.4 refuses a checkout step (the plan save, the session, the attach).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: checkout email hold on the
 * authed lanes (2026-09-26). The rule, where the 403 lands on the server and the
 * money rule are in `checkoutOwnership.ts`; read its header first.
 *
 * THREE SCREENS, ALL CALM (never the danger tone, never `role="alert"`, never
 * "please try again" or "link expired"):
 *  - `email_not_verified` — "Confirm your email to continue", the masked address
 *    the link went to, and the SHARED `CheckEmailCard` in its checkout-step
 *    variant: "I’ve confirmed it — continue" (the caller re-reads the session and
 *    retries THE SAME STEP) and "Send the link again" (identity's existing
 *    resend, with its rest period and 429 words). No second resend card exists.
 *  - `email_not_verified` while the account's OWN session says verified
 *    (`accountSaysVerified`, review 2026-09-26) — "We couldn’t confirm your
 *    account just now", no mailbox words and no resend (identity sends nothing
 *    to a verified account, so both would be false), a way to reach us, and one
 *    Continue that re-sends the same step. Why orders can say that is in
 *    `checkoutOwnership.ts`.
 *  - `checkout_not_owner` — "This order was started with a different email
 *    address", the way on in one sentence, and two actions: Sign out (the app's
 *    own sign-out, landing on the SIGN-IN screen with `?next=` back to this
 *    checkout, so "sign in with that email" leads somewhere) and Start again (a
 *    fresh journey document at this clinic's entry).
 *
 * WHY START AGAIN IS A PLAIN ANCHOR, a full document load and not an in-app
 * restart: everything this document holds in memory — the lead, the assessment,
 * its grant, the answers — belongs to the check this account may not continue,
 * and the address bar may carry `?assessment=` for it, which a client-side
 * restart would leave behind for the next reload to restore. A new document
 * starts clean. It stays inside the tracked journey zone, so it is not a
 * `ZoneExitLink` case.
 *
 * FOCUS. The screen replaces the control the patient just pressed (Continue to
 * delivery, the safety tick, Place order), so the caller hands the keyboard to
 * the heading here (`titleId`, `tabIndex={-1}`, never a Tab stop). The "not yet
 * confirmed" line lives in a status region that is in the DOM from the first
 * render and gains its words when needed; the button that asked keeps focus
 * (it is `aria-disabled` while it works, never `disabled`).
 */
import { CheckEmailCard } from '../auth/CheckEmailCard';
import { withBasename } from '../config';
import { maskEmail } from '../lib/maskEmail';
import { Alert, Button, Help, Title, buttonClass, buttonStyle } from '../ui/kit';
import type { ReactNode } from 'react';
import {
  ACCOUNT_CHECK_BODY,
  ACCOUNT_CHECK_CONTINUE,
  ACCOUNT_CHECK_STILL,
  ACCOUNT_CHECK_TITLE,
  CHECKOUT_NOT_OWNER,
  EMAIL_HOLD_LINE_AFTER,
  EMAIL_HOLD_LINE_BEFORE,
  EMAIL_HOLD_LINE_NO_ADDRESS,
  EMAIL_HOLD_NOT_YET,
  EMAIL_HOLD_TITLE,
  NOT_OWNER_BODY,
  NOT_OWNER_SIGN_OUT,
  NOT_OWNER_START_AGAIN,
  NOT_OWNER_TITLE,
  type CheckoutOwnershipRefusal,
} from './checkoutOwnership';

export function CheckoutOwnershipHold({
  refusal,
  accountSaysVerified = false,
  email,
  notYetConfirmed,
  busy,
  onContinue,
  onSignOut,
  clinicSlug,
  titleId,
  progress,
}: {
  refusal: CheckoutOwnershipRefusal;
  /**
   * orders said `email_not_verified` while the account's own session said
   * verified — the account-check screen, never the email hold.
   */
  accountSaysVerified?: boolean;
  /** The signed-in account's address (`me.email`); absent on an older identity. */
  email?: string;
  /** orders refused again after this screen's continue. */
  notYetConfirmed: boolean;
  /** The re-read + retry is running. */
  busy: boolean;
  /** Re-read the session, then retry the refused step. */
  onContinue: () => void;
  /** The app's own sign-out, back to this checkout after sign-in. */
  onSignOut: () => void;
  /** This clinic's journey entry is where Start again goes. */
  clinicSlug: string;
  /** The heading's id — the caller focuses it when the screen appears. */
  titleId: string;
  /** The checkout's step indicator, as the refused step drew it. */
  progress: ReactNode;
}) {
  if (refusal === CHECKOUT_NOT_OWNER) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        {progress}
        <Title id={titleId} tabIndex={-1}>
          {NOT_OWNER_TITLE}
        </Title>
        <Help>{NOT_OWNER_BODY}</Help>
        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
          <Button type="button" variant="primary" onClick={onSignOut} style={{ width: 'auto' }}>
            {NOT_OWNER_SIGN_OUT}
          </Button>
          <a
            href={withBasename(`/${clinicSlug}`)}
            className={buttonClass('ghost')}
            style={{ ...buttonStyle('ghost'), width: 'auto', textDecoration: 'none' }}
          >
            {NOT_OWNER_START_AGAIN}
          </a>
        </div>
      </div>
    );
  }

  if (accountSaysVerified) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        {progress}
        <Title id={titleId} tabIndex={-1}>
          {ACCOUNT_CHECK_TITLE}
        </Title>
        <Help>{ACCOUNT_CHECK_BODY}</Help>
        {/* In the DOM from the first render, so a repeat answer is announced;
            the Continue that asked keeps focus. */}
        <div role="status">
          {notYetConfirmed ? (
            <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {ACCOUNT_CHECK_STILL}
            </Alert>
          ) : null}
        </div>
        <Button
          type="button"
          variant="primary"
          aria-disabled={busy || undefined}
          onClick={() => {
            if (!busy) onContinue();
          }}
          style={{ width: 'auto' }}
        >
          {busy ? 'Checking…' : ACCOUNT_CHECK_CONTINUE}
        </Button>
      </div>
    );
  }

  const masked = maskEmail(email);
  const lead = (
    <Help>
      {masked ? (
        <>
          {EMAIL_HOLD_LINE_BEFORE}
          {/* Drawn masked; read as words — three "bullet"s say nothing. */}
          <strong style={{ color: 'var(--gps-text)', fontWeight: 600 }}>
            <span aria-hidden="true">{masked.shown}</span>
            <span className="gps-sr-only">{masked.spoken}</span>
          </strong>
          {EMAIL_HOLD_LINE_AFTER}
        </>
      ) : (
        EMAIL_HOLD_LINE_NO_ADDRESS
      )}
    </Help>
  );

  return (
    <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
      {progress}
      <Title id={titleId} tabIndex={-1}>
        {EMAIL_HOLD_TITLE}
      </Title>
      {/* In the DOM from the first render and empty until orders refuses AGAIN,
          so the line is announced when it arrives. `info`, never the danger
          tone: nothing failed, the link simply has not been opened yet. */}
      <div role="status">
        {notYetConfirmed ? (
          <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {EMAIL_HOLD_NOT_YET}
          </Alert>
        ) : null}
      </div>
      <CheckEmailCard email={email} lead={lead} onContinue={onContinue} continueBusy={busy} />
    </div>
  );
}
